import React, { useState, useEffect } from 'react';
import { customFields } from '../../utils/api/customFields';

const CustomFieldsSection = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    max_length: 2000  // Keep max_length in state but don't show in UI
  });

  // Load custom fields
  useEffect(() => {
    loadFields();
  }, []);

  const loadFields = async () => {
    try {
      const response = await customFields.getAll();
      setFields(response);
      setError(null);
    } catch (err) {
      console.error('Error loading custom fields:', err);
      setError('Failed to load custom fields');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      if (formData.id) {
        await customFields.update(formData.id, formData);
      } else {
        await customFields.create(formData);
      }
      await loadFields();
      setShowForm(false);
      setFormData({ name: '', max_length: 2000 });
    } catch (err) {
      console.error('Error saving custom field:', err);
      setError(err.response?.data?.error || 'Failed to save custom field');
    }
  };

  const handleEdit = (field) => {
    setFormData({
      id: field.id,
      name: field.name,
      max_length: field.max_length
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this custom field? All values for this field will be permanently deleted.')) {
      return;
    }

    try {
      await customFields.delete(id);
      await loadFields();
    } catch (err) {
      console.error('Error deleting custom field:', err);
      setError(err.response?.data?.error || 'Failed to delete custom field');
    }
  };

  if (loading) {
    return <div>Loading custom fields...</div>;
  }

  return (
    <div className="space-y-6 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
      <div>
        <h2 className="text-lg font-medium leading-6 text-gray-900 mb-4">Custom Fields</h2>
        <p className="text-sm text-gray-500">
          Create custom fields to store additional information about your coins. These fields will be available when adding or editing coins.
        </p>
        
        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Field List */}
        <div className="mt-4">
          {fields.length === 0 && !showForm ? (
            <p className="text-gray-500">No custom fields defined yet.</p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {fields.map(field => (
                <li key={field.id} className="py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium">{field.name}</h3>
                      <p className="text-sm text-gray-500">
                        {field.usage_count > 0 && `Used in ${field.usage_count} coin${field.usage_count === 1 ? '' : 's'}`}
                      </p>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleEdit(field)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(field.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Add/Edit Form */}
        {showForm ? (
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Field Name
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                required
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setFormData({ name: '', max_length: 2000 });
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {formData.id ? 'Update Field' : 'Create Field'}
              </button>
            </div>
          </form>
        ) : (
          <button
            onClick={() => setShowForm(true)}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Add Custom Field
          </button>
        )}
      </div>
    </div>
  );
};

export default CustomFieldsSection;
